import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
    CellClickedEvent,
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    RowClassParams,
    ValueFormatterParams,
} from 'ag-grid-community';
import { RootState, useGetPartsZipComponentDetailsMutation, useGetPartsHotspotViewMutation } from "@circuitry-ai/doc-data";
import { PartsSvgViewer, NoDiagramPartTooltip } from "./partssvgviewer";
import { Icon } from '@iconify/react';
import CtySpinner from '../spinner/CtySpinner';
import { ResizableBox, ResizableBoxProps } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { CartIcon } from '../utils/cartActions';
import PartsImageViewer from './partsimageviewer';
import { CtyShowToast } from '../toast/CtyToast';
import { useSelector } from 'react-redux';
import { Breadcrumbs, BreadcrumbItem } from '@nextui-org/react';
import { MetadataInfoButton } from './MetadataInfoButton';

// Use the ResizableBox as a function component to fix the typing issue
const ResizablePanel = (props: ResizableBoxProps & { children: React.ReactNode }) => {
    return <ResizableBox {...props}>{props.children}</ResizableBox>;
};

// One clickable segment of the breadcrumb trail. `groupName`/`path` are whatever
// onComponentClick needs to re-open that segment's own diagram/component.
export interface BreadcrumbCrumb {
    label: string;
    groupName: string;
    path?: string;
}

// One entry in a component/group's `diagrams` list (the currently open item can have
// more than one image; these back the prev/next nav arrows in the viewer toolbar).
export interface DiagramRef {
    file_name: string;
    source_id?: string | null;
}

export interface ComponentNode {
    name: string;
    path: string;
    file_name?: string;
    source_id?: string | null;
    [key: string]: any;
}

export interface ComponentGroup {
    component_name: string;
    components: ComponentNode[];
    // N-level nesting: a group may contain further nested groups.
    sub_groups?: ComponentGroup[];
    [key: string]: any;
}

function getSubGroups(group: ComponentGroup | null | undefined): ComponentGroup[] {
    if (!group) return [];
    if (Array.isArray(group.sub_groups)) return group.sub_groups;
    return [];
}

// The path a component click looks up. Some catalogs (e.g. Excel-derived ones)
// return `path: ""` for their components, which used to make the row inert — the
// click was gated on a truthy path and `handleFileOpen` rejects an empty one. Fall
// back to the component's own name, exactly like `groupPath` does for groups: the
// parts request resolves from component_group + component_name anyway (it
// intentionally sends no `filename`), so a name is a sufficient lookup.
function getComponentLookupPath(component: ComponentNode | null | undefined): string {
    if (!component) return '';
    const path = String(component.path ?? '').trim();
    if (path) return path;
    return String(component.name ?? '').trim();
}

// Same ordering the left "Components" panel renders (see ComponentList's
// `sortedData`): drops empty/nameless groups and sorts alphabetically. Shared
// so the initial auto-selected group is always the one actually shown first.
function getSortedComponentGroups(groups: ComponentGroup[] | null | undefined): ComponentGroup[] {
    if (!Array.isArray(groups)) return [];

    return groups
        .filter(group => {
            if (!group || !group.component_name) return false;
            const hasComponents = Array.isArray(group.components) && group.components.length > 0;
            const hasSubGroups = getSubGroups(group).length > 0;
            return hasComponents || hasSubGroups;
        })
        .sort((a, b) => (a.component_name ?? '').toLowerCase().localeCompare((b.component_name ?? '').toLowerCase()));
}

// Finds a component-group anywhere in an N-level nested group tree by its
// `component_name` (a deep-linked group can be nested several levels down, e.g.
// FFPD > Divert > "9119956D - Tray Assembly") — a plain top-level array search
// (as this used to be) misses anything below the first level.
//
// Also returns the full root-first ancestor path leading to it: the left
// "Components" tree only highlights a node when the FULL breadcrumb path matches
// (see `isSamePath` usage in `ComponentGroupItem`), not just the group's own
// label, so a deep-link breadcrumb built from only the matched group + leaf would
// never actually light up in the tree.
function findGroupWithPath(
    groups: ComponentGroup[] | null | undefined,
    groupName: string,
    ancestorCrumbs: BreadcrumbCrumb[] = []
): { group: ComponentGroup; path: BreadcrumbCrumb[] } | null {
    if (!Array.isArray(groups) || !groupName) return null;

    for (const group of groups) {
        if (!group) continue;
        const ownCrumb: BreadcrumbCrumb = { label: group.component_name, groupName: group.component_name, path: group.component_name };
        const ownPath = [...ancestorCrumbs, ownCrumb];

        if (group.component_name === groupName) {
            return { group, path: ownPath };
        }

        const nested = findGroupWithPath(getSubGroups(group), groupName, ownPath);
        if (nested) return nested;
    }

    return null;
}

export interface ComponentDetails {
    component_name: string;
    referenceNumber: string;
    parts_number: string;
    name: string;
    quantity: string;
    file_name?: string;
    part_file_name?: string;
    source_id?: string;
}

type PartsSelectionSource = 'grid' | 'hotspot';

function getComponentDetailRowKey(detail: Pick<ComponentDetails, 'referenceNumber' | 'parts_number' | 'name'> | null | undefined): string {
    if (!detail) return '';
    return `${detail.referenceNumber}-${detail.parts_number}-${detail.name}`;
}

interface ComponentDetailsPanelProps {
    readonly details?: [ComponentDetails];
    readonly onClose: () => void;
    readonly setActiveComponentDetails: (detail: ComponentDetails) => void;
    readonly activeComponentDetails: ComponentDetails | null;
    readonly svgHighlightData: any;
    readonly selectionSource: PartsSelectionSource | null;
}

export interface ComponentListProps {
    readonly data: ComponentGroup[];
    readonly onComponentClick: (groupName: string, path: string, componentName: string, breadcrumb: BreadcrumbCrumb[]) => void;
    readonly activeFileName: string;
    // Full label path (root-first) of the currently open node, e.g. ["NBASE","FEEDER UNIT","Feeder Tray Assembly"].
    // Used instead of a bare name match so a group and a same-named child component never both light up.
    readonly activeBreadcrumb?: string[];
    readonly searchQuery: string;
}

export interface ComponentGroupItemProps {
    readonly group: ComponentGroup;
    readonly onComponentClick: (groupName: string, path: string, componentName: string, breadcrumb: BreadcrumbCrumb[]) => void;
    readonly activeFileName: string;
    readonly activeBreadcrumb?: string[];
    readonly level?: number;
    // Ancestor crumbs (root-first) leading down to this group, used to build breadcrumbs.
    readonly ancestorCrumbs?: BreadcrumbCrumb[];
}

function isSamePath(a: string[] | undefined, b: string[]): boolean {
    if (!a || a.length !== b.length) return false;
    return a.every((label, index) => label === b[index]);
}

interface ComponentDetailsGridContext {
    svgHighlightData: ComponentDetailsPanelProps['svgHighlightData'];
    selectionSource: PartsSelectionSource | null;
}

function isComponentDetailHighlighted(
    detail: ComponentDetails | null | undefined,
    svgHighlightData: ComponentDetailsPanelProps['svgHighlightData'],
    selectionSource: PartsSelectionSource | null
): boolean {
    if (!detail || !svgHighlightData?.referenceNumber) return false;
    // Grid click: only the clicked row (unique by ref + part # + name)
    if (selectionSource === 'grid') {
        return getComponentDetailRowKey(detail) === getComponentDetailRowKey(svgHighlightData);
    }
    // Hotspot (or unknown): all rows sharing the reference number
    return detail.referenceNumber === svgHighlightData.referenceNumber;
}

function ComponentDetailsCartCell(params: ICellRendererParams<ComponentDetails>) {
    const data = params.data;
    if (!data) return null;
    const ctx = params.context as ComponentDetailsGridContext | undefined;
    const isActive = isComponentDetailHighlighted(data, ctx?.svgHighlightData, ctx?.selectionSource ?? null);
    return (
        <div className="flex h-full w-full items-center justify-center">
            <CartIcon
                partNumber={data.parts_number}
                partName={data.name}
                referenceNumber={data.referenceNumber}
                isActive={isActive}
            />
        </div>
    );
}

function ComponentDetailsPanel({ details, onClose, setActiveComponentDetails, activeComponentDetails, svgHighlightData, selectionSource }: ComponentDetailsPanelProps) {
    const [searchQuery, setSearchQuery] = useState('');
    //active ref was replaced with gridApiRef which was helped to bring the active row in to the viewport
    const gridApiRef = useRef<GridApi<ComponentDetails> | null>(null);

    // Get cart state from Redux - keep this for checking cart status

    // Remove duplicates based on combination of reference number, part number, and part name
    const uniqueDetails = useMemo(() => {
        if (!details) return [];

        const uniqueMap = new Map();

        details.forEach(detail => {
            const uniqueKey = `${detail.referenceNumber}-${detail.parts_number}-${detail.name}`;
            if (!uniqueMap.has(uniqueKey)) {
                uniqueMap.set(uniqueKey, detail);
            }
        });

        return Array.from(uniqueMap.values());
    }, [details]);

    // Filter component details based on search query
    const filteredDetails = useMemo(() => {
        if (!uniqueDetails || !searchQuery.trim()) return uniqueDetails;

        const query = searchQuery.toLowerCase().trim();
        return uniqueDetails.filter(detail =>
            detail.name.toLowerCase().includes(query) ||
            detail.parts_number.toLowerCase().includes(query)
        );
    }, [uniqueDetails, searchQuery]);


    const handleComponentHighlight = useCallback((detail: ComponentDetails) => {
        setActiveComponentDetails(detail);
    }, [setActiveComponentDetails]
    );

    const columnDefs = useMemo(
        () =>
            [
                {
                    colId: 'referenceNumber',
                    headerName: 'Ref #',
                    field: 'referenceNumber' as const,
                    width: 90,
                    minWidth: 60,
                    maxWidth: 120,
                    sortable: true,
                },
                {
                    colId: 'parts_number',
                    headerName: 'Part #',
                    field: 'parts_number' as const,
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                },
                {
                    colId: 'name',
                    headerName: 'Part Name',
                    field: 'name' as const,
                    flex: 2,
                    minWidth: 120,
                    sortable: true,
                    tooltipField: 'name' as const,
                },
                {
                    colId: 'cart',
                    headerName: 'Cart',
                    width: 80,
                    minWidth: 70,
                    maxWidth: 100,
                    sortable: false,
                    suppressMovable: true,
                    cellRenderer: ComponentDetailsCartCell,
                    headerClass: 'parts-zip-details-cart-header',
                },
            ] as ColDef<ComponentDetails>[],
        []
    );

    const getRowStyle = useCallback(
        (params: RowClassParams<ComponentDetails>) => {
            if (isComponentDetailHighlighted(params.data, svgHighlightData, selectionSource)) {
                return { backgroundColor: '#003a79', color: '#fff' };
            }
            return undefined;
        },
        [svgHighlightData, selectionSource]
    );

    const onGridReady = useCallback((e: GridReadyEvent<ComponentDetails>) => {
        gridApiRef.current = e.api;
    }, []);

    const onCellClicked = useCallback(
        (e: CellClickedEvent<ComponentDetails>) => {
            if (e.column?.getColId() === 'cart' || !e.data) return;
            handleComponentHighlight(e.data);
        },
        [handleComponentHighlight]
    );

    useEffect(() => {
        const api = gridApiRef.current;
        if (!api || !activeComponentDetails) return;
        const activeKey = getComponentDetailRowKey(activeComponentDetails);
        api.forEachNode((node) => {
            if (!node.data) return;
            const matches =
                selectionSource === 'grid'
                    ? getComponentDetailRowKey(node.data) === activeKey
                    : node.data.referenceNumber === activeComponentDetails.referenceNumber;
            if (matches) {
                api.ensureNodeVisible(node, 'middle');
            }
        });
    }, [activeComponentDetails, selectionSource]);

    useEffect(() => {
        gridApiRef.current?.redrawRows();
    }, [svgHighlightData, selectionSource]);

    if (!details) return null;
    return (
        <div className="h-full bg-content1 flex flex-col rounded-lg">
            {/* Header */}
            <div className="flex-none p-4 border-b border-default-200 flex justify-between items-center bg-content2 rounded-t-lg h-[52px]">
                <h2 className="text-base font-medium text-foreground">Component Details</h2>
                <button onClick={onClose} className="p-1 hover:bg-default-100 rounded-full" title="Collapse">
                    <Icon icon="hugeicons:layout-right" width="20" height="20" className="text-foreground" />
                </button>
            </div>

            {/* Search Bar */}
            <div className="p-3 border-b border-default-200">
                <div className="relative">
                    <input
                        type="text"
                        className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Search by part name or number..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute left-3 top-2.5 text-default-400">
                        <Icon icon="ri:search-line" width="18" height="18" />
                    </div>
                    {searchQuery && (
                        <button
                            className="absolute right-3 top-2.5 text-default-400 hover:text-default-500"
                            onClick={() => setSearchQuery('')}
                        >
                            <Icon icon="ri:close-line" width="18" height="18" />
                        </button>
                    )}
                </div>
            </div>

            {/* Component list grid */}
            <div className="flex min-h-0 flex-1 flex-col">
                {filteredDetails.length > 0 ? (
                    <div className="parts-zip-details-grid ag-theme-alpine min-h-[120px] flex-1" style={{}}>
                        <AgGridReact<ComponentDetails>
                            rowData={filteredDetails}
                            columnDefs={columnDefs}
                            defaultColDef={{ resizable: true, sortable: true, }}
                            context={{ svgHighlightData, selectionSource } satisfies ComponentDetailsGridContext}
                            getRowId={(p) => getComponentDetailRowKey(p.data)}
                            getRowStyle={getRowStyle}
                            headerHeight={44}
                            rowHeight={44}
                            suppressCellFocus
                            domLayout="normal"
                            onGridReady={onGridReady}
                            onCellClicked={onCellClicked}
                            rowClass="cursor-pointer"
                            tooltipShowDelay={0}
                        />
                    </div>
                ) : (
                    <div className="px-4 py-4 text-center text-sm text-default-500">
                        {searchQuery.trim()
                            ? 'No components match your search criteria'
                            : 'No component details available'}
                    </div>
                )}
            </div>
        </div>
    );
}

interface EmptyDetailsPanelProps {
    readonly onClose: () => void;
}

function EmptyDetailsPanel({ onClose }: EmptyDetailsPanelProps) {
    return (
        <div className="h-full bg-content1 flex flex-col rounded-lg">
            <div className="flex-none p-4 border-b border-default-200 flex justify-between items-center bg-content2 rounded-t-lg h-[52px]">
                <h2 className="text-base font-medium text-foreground">Component Details</h2>
                <button onClick={onClose} className="p-1 hover:bg-default-100 rounded-full" title="Collapse">
                    <Icon icon="hugeicons:layout-right" width="20" height="20" className="text-foreground" />
                </button>
            </div>
            <div className="flex-1 flex items-center justify-center">
                <p className="text-foreground">Select a component to view details</p>
            </div>
        </div>
    );
}

interface ZipExplorerProps {
    readonly hierarchyData: any;
    readonly svgUrl: string;
    readonly sourceIdData: any;
    readonly advisorId: string;
    readonly sourceObject: any;
    readonly metadataSourceId?: string;
}

// Coordinates come back either as a flat array (older BE) or wrapped in an
// OCR metadata object with the array nested under `.coordinates` (current BE).
const extractCoordinatesArray = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.coordinates)) return raw.coordinates;
    return [];
};

// Helper function to determine file type
const getFileType = (fileName: string): 'pdf' | 'svg' | 'image' | 'other' => {
    if (!fileName) return 'other';
    const extension = fileName.toLowerCase().split('.').pop() ?? '';
    if (extension === 'pdf') return 'pdf';
    if (extension === 'svg') return 'svg';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
    return 'other';
};

// The API can resolve a request to a different actual file than the requested
// filename implied (e.g. request "accessories_diagram.png" back a "ACCESSORIES.svg"
// presigned URL). Strip the query/hash before reading the extension off the real URL,
// since that's the file that's actually going to be rendered.
const getFileTypeFromUrl = (url: string | undefined | null): 'pdf' | 'svg' | 'image' | 'other' => {
    if (!url) return 'other';
    const pathOnly = url.split('?')[0].split('#')[0];
    return getFileType(pathOnly);
};

function ZipFileExplorer({
    hierarchyData,
    svgUrl,
    sourceIdData,
    advisorId,
    sourceObject,
    metadataSourceId,
}: ZipExplorerProps) {
    // This state holds the currently active file's information
    const [activeFile, setActiveFile] = useState<{
        url: string;
        type: 'pdf' | 'svg' | 'image' | 'other';
        name: string;
        details?: [ComponentDetails];
        coordinates?: any[];
        breadcrumb?: BreadcrumbCrumb[];
        diagrams?: DiagramRef[];
        diagramIndex?: number;
    }>({
        url: svgUrl,
        type: getFileType(svgUrl),
        name: ''
    });

    const catalogSourceId = sourceObject?.source_id ?? sourceIdData;

    // Panel state
    const [leftPanelOpen, setLeftPanelOpen] = useState(true);
    const [rightPanelOpen, setRightPanelOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    // Separate from `isLoading`: prev/next diagram nav only swaps the image/coordinates,
    // the parts list stays put, so it must not trigger the Component Details loader.
    const [isDiagramLoading, setIsDiagramLoading] = useState(false);
    const [getZipComponents] = useGetPartsZipComponentDetailsMutation();
    const [getHotspotView] = useGetPartsHotspotViewMutation();
    const [activeComponentDetails, setActiveComponentDetails] = useState<ComponentDetails | null>(null);
    const [leftPanelSearchQuery, setLeftPanelSearchQuery] = useState('');
    const token = useSelector((state: RootState) => state.auth.token);


    // Use a ref to track if content has been initially loaded
    const [hasInitialContent, setHasInitialContent] = useState(false);

    // When deep-linked with a specific Ref# / Part Number (not just a component
    // group/name), remembers that part so it can be auto-highlighted in the
    // Component Details grid once that component's details have finished loading.
    const [pendingDeepLinkPart, setPendingDeepLinkPart] = useState<{ referenceNumber?: string; parts_number?: string } | null>(null);

    // Create a new state for SVG highlighting that doesn't trigger re-rendering of the entire SVG
    const [svgHighlightData, setSvgHighlightData] = useState<ComponentDetails | null>(null);
    const [partsRowSelectToken, setPartsRowSelectToken] = useState(0);
    const [partsSelectionSource, setPartsSelectionSource] = useState<PartsSelectionSource | null>(null);

    const selectComponentDetail = useCallback((detail: ComponentDetails | null, source: PartsSelectionSource = 'hotspot') => {
        if (!detail) return;
        setActiveComponentDetails(detail);
        setSvgHighlightData(detail);
        setPartsSelectionSource(source);
        setPartsRowSelectToken((prev) => prev + 1);
    }, []);

    const selectComponentDetailFromGrid = useCallback((detail: ComponentDetails) => {
        selectComponentDetail(detail, 'grid');
    }, [selectComponentDetail]);

    const selectComponentDetailFromHotspot = useCallback((detail: ComponentDetails) => {
        selectComponentDetail(detail, 'hotspot');
    }, [selectComponentDetail]);

    const [leftPanelWidth, setLeftPanelWidth] = useState(300); // 20% of typical 1500px width
    const [rightPanelWidth, setRightPanelWidth] = useState(450); // 30% of typical 1500px width

    // Navigates to the prev/next entry in the currently open item's `diagrams` list via
    // the hotspot-view endpoint, swapping only the image + coordinates (parts list and
    // breadcrumb stay put since it's still the same component/group).
    const handleDiagramNavigate = useCallback(async (direction: 'prev' | 'next') => {
        const diagrams = activeFile.diagrams ?? [];
        const currentIndex = activeFile.diagramIndex ?? 0;
        const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
        const targetDiagram = diagrams[newIndex];
        if (!targetDiagram?.file_name) return;

        if (!token) {
            CtyShowToast('info', 'Please wait for authentication to complete');
            return;
        }

        const sourceId = sourceObject?.source_id ?? sourceIdData;
        if (!sourceId) return;

        setIsDiagramLoading(true);
        try {
            const hotspotView = await getHotspotView({
                source_id: sourceId,
                component_name: activeFile.name,
                file_name: targetDiagram.file_name
            }).unwrap();

            const resolvedFileType = getFileTypeFromUrl(hotspotView?.presigned_url);
            setActiveFile(prev => ({
                ...prev,
                url: hotspotView?.presigned_url ?? prev.url,
                type: resolvedFileType !== 'other' ? resolvedFileType : prev.type,
                coordinates: extractCoordinatesArray(hotspotView?.coordinates),
                diagramIndex: newIndex
            }));
        } catch (error: unknown) {
            console.error("Error loading hotspot view:", error);
            CtyShowToast('error', 'Unable to load image. Please try again.');
        } finally {
            setIsDiagramLoading(false);
        }
    }, [activeFile.diagrams, activeFile.diagramIndex, activeFile.name, token, sourceObject, sourceIdData, getHotspotView]);

    const handlePrevDiagram = useCallback(() => handleDiagramNavigate('prev'), [handleDiagramNavigate]);
    const handleNextDiagram = useCallback(() => handleDiagramNavigate('next'), [handleDiagramNavigate]);

    const diagramCount = activeFile.diagrams?.length ?? 0;
    const diagramIndex = activeFile.diagramIndex ?? 0;
    const hasMultipleDiagrams = diagramCount > 1;
    const canGoPrevDiagram = diagramIndex > 0 && !isDiagramLoading;
    const canGoNextDiagram = diagramIndex < diagramCount - 1 && !isDiagramLoading;

    // Memoize the rendered content to prevent rerendering during search and component detail selection
    const memoizedContent = useMemo(() => {
        // Show loading state while waiting for token
        if (!token) {
            return (
                <div className="flex h-full w-full items-center justify-center flex-col">
                    <CtySpinner size="lg" />
                    <p className="mt-4 text-foreground text-sm">Waiting for authentication...</p>
                </div>
            );
        }

        if (isLoading || isDiagramLoading) {
            return (
                <div className="flex h-full w-full items-center justify-center">
                    <CtySpinner size="lg" />
                </div>
            );
        }

        if (activeFile && activeFile.url) {
            switch (activeFile.type) {
                case 'svg':
                    return (
                        <div className="h-full w-full overflow-auto relative">
                            <div
                                style={{
                                    transformOrigin: 'top left',
                                    width: '100%',
                                    height: '100%'
                                }}
                            >
                                <PartsSvgViewer
                                    svgUrl={activeFile.url}
                                    isFileViewer={true}
                                    currentActivePartsRowData={[svgHighlightData]}
                                    setSelectedRowId={selectComponentDetailFromHotspot}
                                    activeSourceData={[]}
                                    filename={activeFile.name}
                                    partsListFromFileViewer={activeFile.details ?? []}
                                    fileType="svg"
                                    componentDetails={activeFile.details ?? []}
                                    catalogSourceId={catalogSourceId}
                                    partsRowSelectToken={partsRowSelectToken}
                                    onPrevImage={hasMultipleDiagrams ? handlePrevDiagram : undefined}
                                    onNextImage={hasMultipleDiagrams ? handleNextDiagram : undefined}
                                    canGoPrevImage={canGoPrevDiagram}
                                    canGoNextImage={canGoNextDiagram}
                                />
                            </div>
                        </div>
                    );
                case 'image':
                    return (
                        <div className="h-full w-full overflow-auto relative">
                            <div
                                style={{
                                    transformOrigin: 'top left',
                                    width: '100%',
                                    height: '100%'
                                }}
                            >
                                <PartsImageViewer
                                    imageUrl={activeFile.url}
                                    isFileViewer={true}
                                    currentActivePartsRowData={[svgHighlightData]}
                                    setSelectedRowId={selectComponentDetailFromHotspot}
                                    filename={activeFile.name}
                                    setSvgHighlightData={setSvgHighlightData}
                                    coordinates={activeFile.coordinates}
                                    componentDetails={activeFile.details}
                                    highlightedComponent={svgHighlightData}
                                    highlightedReferenceNumbers={undefined}
                                    catalogSourceId={catalogSourceId}
                                    partsRowSelectToken={partsRowSelectToken}
                                    onPrevImage={hasMultipleDiagrams ? handlePrevDiagram : undefined}
                                    onNextImage={hasMultipleDiagrams ? handleNextDiagram : undefined}
                                    canGoPrevImage={canGoPrevDiagram}
                                    canGoNextImage={canGoNextDiagram}
                                />
                            </div>
                        </div>
                    );
                case 'pdf':
                    return (
                        <div className="h-full w-full overflow-auto relative">
                            <iframe
                                src={activeFile.url}
                                className="w-full h-[calc(100%-52px)]"
                                title={activeFile.name}
                            />
                        </div>
                    );
                default:
                    return (
                        <NoDiagramPartTooltip
                            message="No images available."
                            currentActivePartsRowData={[svgHighlightData]}
                            isFileViewer={true}
                            componentDetails={activeFile.details}
                            catalogSourceId={catalogSourceId}
                            partsRowSelectToken={partsRowSelectToken}
                            setSelectedRowId={selectComponentDetailFromHotspot}
                        />
                    );
            }
        } else if (activeFile.name) {
            // A component was selected (auto or by click) and the fetch already
            // finished (isLoading/isDiagramLoading were checked above) — it simply
            // has no diagram to show, so say that instead of spinning forever.
            return (
                <NoDiagramPartTooltip
                    message="No images available."
                    currentActivePartsRowData={[svgHighlightData]}
                    isFileViewer={true}
                    componentDetails={activeFile.details}
                    catalogSourceId={catalogSourceId}
                    partsRowSelectToken={partsRowSelectToken}
                    setSelectedRowId={selectComponentDetailFromHotspot}
                />
            );
        } else {
            return (
                <div className="flex h-full items-center justify-center text-foreground">
                    Please select a component to view
                </div>
            );
        }
    }, [activeFile, isLoading, isDiagramLoading, svgHighlightData, partsRowSelectToken, token, catalogSourceId, selectComponentDetailFromHotspot, hasMultipleDiagrams, handlePrevDiagram, handleNextDiagram, canGoPrevDiagram, canGoNextDiagram]);

    // Keep svgHighlightData in sync when activeComponentDetails is set externally (e.g. initial load)
    useEffect(() => {
        if (activeComponentDetails) {
            setSvgHighlightData(activeComponentDetails);
        }
    }, [activeComponentDetails]);

    // Original renderContent function now returns the memoized content
    const renderContent = () => {
        return memoizedContent;
    };


    useEffect(() => {
        if (sourceObject) {
            console.log("ZipFileExplorer received sourceObject:", sourceObject.advisor_id);
        }
    }, [sourceObject]);

    // Called when a file node is clicked
    const handleFileOpen = useCallback(async (groupName: string, path: string, componentName: string, breadcrumb: BreadcrumbCrumb[] = []) => {
        if (!path) return;

        // Check if token is available before making API call
        if (!token) {
            console.log('Token not available, waiting for authentication...');
            CtyShowToast('info', 'Please wait for authentication to complete');
            return;
        }

        // Reset active component details and highlighting when switching components
        setActiveComponentDetails(null);
        setSvgHighlightData(null);
        setPartsSelectionSource(null);

        setIsLoading(true);
        // Best-guess from the requested filename; the fetch below may resolve to a
        // different actual file (e.g. a .png request can come back as a .svg), so
        // this is only a fallback for when the response has no presigned_url yet.
        const fallbackFileType = getFileType(path);

        try {
            // Use the advisor_id from sourceObject if available for authentication context
            const sourceId = sourceObject?.source_id ?? sourceIdData;

            if (!sourceId) {
                CtyShowToast('error', 'Source ID is required to fetch component details');
                setIsLoading(false);
                return;
            }

            // Construct request params including all necessary context
            const requestParams = {
                source_id: sourceId,
                componentGroup: groupName,
                componentName
                // Intentionally no `filename`: the backend resolves the file to show
                // from componentGroup + componentName alone; sending file_name isn't wanted.
            };

            console.log('requestParams ----------', requestParams);
            console.log('token ----------', token);
            const zipComponents = await getZipComponents(requestParams).unwrap();
            const resolvedFileType = getFileTypeFromUrl(zipComponents?.presigned_url);
            setActiveFile({
                url: zipComponents?.presigned_url ?? '',
                type: resolvedFileType !== 'other' ? resolvedFileType : fallbackFileType,
                name: componentName,
                details: zipComponents?.component_details ?? [],
                coordinates: extractCoordinatesArray(zipComponents?.coordinates),
                breadcrumb,
                diagrams: Array.isArray(zipComponents?.diagrams) ? zipComponents.diagrams : [],
                diagramIndex: 0
            });
        } catch (error: unknown) {
            console.error("Error handling file:", error);
            if (error && typeof error === 'object' && 'response' in error) {
                const errorWithResponse = error as { response: { status: string, data: unknown } };
                console.error("Error response:", errorWithResponse.response.status, errorWithResponse.response.data);
            }
            CtyShowToast('error', `Unable to load "${componentName}". Please try again.`);
        } finally {
            setIsLoading(false);
        }
    }, [token, sourceObject, sourceIdData, getZipComponents]);

    // Auto-select the initial component. If the caller provided explicit deep-link
    // context (a specific component group + name, via `sourceObject.zip_components_details`
    // — e.g. the user clicked a specific part in chat before opening this explorer),
    // honor that. Otherwise (e.g. opened fresh from the Knowledge section) default to
    // the first group shown in the Components panel, so it's already selected/highlighted
    // with its details visible instead of requiring a manual click.
    useEffect(() => {
        if (!token || hasInitialContent || !hierarchyData?.length) {
            return;
        }

        const firstComponent = sourceObject?.zip_components_details?.[0];
        if (firstComponent) {
            // The deep-linked group can be nested several levels down (e.g. FFPD >
            // Divert > "9119956D - Tray Assembly"), so this has to search the whole
            // tree, not just the top-level `hierarchyData` array — and it needs the
            // full ancestor path too, so the left tree can actually highlight it.
            const matched = findGroupWithPath(hierarchyData, firstComponent.component_group);
            const matchingGroup = matched?.group;

            const matchingComponent = matchingGroup?.components?.find(
                (comp: ComponentNode) => comp.name === firstComponent.component_name
            );

            // Same name fallback as the tree rows: an empty `path` must not send the
            // deep link down the "no match" route and open the first group instead.
            const matchingComponentPath = getComponentLookupPath(matchingComponent);

            if (matchingGroup && matched && matchingComponentPath) {
                const breadcrumb: BreadcrumbCrumb[] = [
                    ...matched.path,
                    { label: matchingComponent!.name, groupName: matchingGroup.component_name, path: matchingComponentPath },
                ];
                handleFileOpen(
                    matchingGroup.component_name,
                    matchingComponentPath,
                    matchingComponent!.name,
                    breadcrumb
                );
                // Also remember the specific Ref# / Part Number the user clicked in chat,
                // so it can be auto-selected in the Component Details grid once this
                // component's details finish loading (see the effect right below).
                if (firstComponent.referenceNumber || firstComponent.parts_number) {
                    setPendingDeepLinkPart({
                        referenceNumber: firstComponent.referenceNumber,
                        parts_number: firstComponent.parts_number,
                    });
                }
                setHasInitialContent(true);
                return;
            }
        }

        // No deep-link match (or none provided) — fall back to the first group as
        // ordered in the Components panel (see `getSortedComponentGroups`), opening
        // it exactly as a click on its group name would (see `handleGroupNameClick`).
        const firstGroup = getSortedComponentGroups(hierarchyData)[0];
        if (firstGroup) {
            const breadcrumb: BreadcrumbCrumb[] = [
                { label: firstGroup.component_name, groupName: firstGroup.component_name, path: firstGroup.component_name },
            ];
            handleFileOpen(firstGroup.component_name, firstGroup.component_name, firstGroup.component_name, breadcrumb);
            setHasInitialContent(true);
        }
    }, [token, hasInitialContent, hierarchyData, sourceObject, handleFileOpen]);

    // Once the deep-linked component's details have loaded, auto-select the
    // specific part row (matched by Ref# + Part Number) the user actually clicked
    // in chat — not just open the component's diagram.
    useEffect(() => {
        if (!pendingDeepLinkPart || !activeFile.details?.length) return;

        const match = activeFile.details.find((detail) => {
            const refMatches = pendingDeepLinkPart.referenceNumber
                ? String(detail.referenceNumber).trim() === String(pendingDeepLinkPart.referenceNumber).trim()
                : true;
            const partMatches = pendingDeepLinkPart.parts_number
                ? String(detail.parts_number).trim() === String(pendingDeepLinkPart.parts_number).trim()
                : true;
            return refMatches && partMatches;
        });

        if (match) {
            selectComponentDetail(match, 'grid');
        }
        setPendingDeepLinkPart(null);
    }, [pendingDeepLinkPart, activeFile.details, selectComponentDetail]);

    const renderRightPanel = () => {
        if (!activeFile) {
            return null;
        }

        if (activeFile.details?.length) {
            return (
                <ComponentDetailsPanel
                    details={activeFile.details}
                    onClose={() => setRightPanelOpen(false)}
                    setActiveComponentDetails={selectComponentDetailFromGrid}
                    activeComponentDetails={activeComponentDetails}
                    svgHighlightData={svgHighlightData}
                    selectionSource={partsSelectionSource}
                />
            );
        }

        return <EmptyDetailsPanel onClose={() => setRightPanelOpen(false)} />;
    };

    const handleComponentClick = useCallback(async (groupName: string, path: string, componentName: string, breadcrumb: BreadcrumbCrumb[] = []) => {
        if (!path) return;

        // Check if token is available before making API call
        if (!token) {
            console.log('Token not available, waiting for authentication...');
            CtyShowToast('info', 'Please wait for authentication to complete');
            return;
        }

        // Reset active component details and highlighting when switching components
        setActiveComponentDetails(null);
        setSvgHighlightData(null);
        setPartsSelectionSource(null);

        setIsLoading(true);
        const fallbackFileType = getFileType(path);

        try {
            // Use the advisor_id from sourceObject if available for authentication context
            const sourceId = sourceObject?.source_id ?? sourceIdData;

            // Construct request params including all necessary context
            const requestParams = {
                source_id: sourceId,
                componentGroup: groupName,
                componentName
                // Intentionally no `filename`: the backend resolves the file to show
                // from componentGroup + componentName alone; sending file_name isn't wanted.
            };

            const zipComponents = await getZipComponents(requestParams).unwrap();
            const resolvedFileType = getFileTypeFromUrl(zipComponents?.presigned_url);
            setActiveFile({
                url: zipComponents?.presigned_url ?? '',
                type: resolvedFileType !== 'other' ? resolvedFileType : fallbackFileType,
                name: componentName,
                details: zipComponents?.component_details ?? [],
                coordinates: extractCoordinatesArray(zipComponents?.coordinates),
                breadcrumb,
                diagrams: Array.isArray(zipComponents?.diagrams) ? zipComponents.diagrams : [],
                diagramIndex: 0
            });
        } catch (error: unknown) {
            console.error("Error handling file:", error);
            if (error && typeof error === 'object' && 'response' in error) {
                const errorWithResponse = error as { response: { status: string, data: unknown } };
                console.error("Error response:", errorWithResponse.response.status, errorWithResponse.response.data);
            }
            CtyShowToast('error', `Unable to load "${componentName}". Please try again.`);
        } finally {
            setIsLoading(false);
        }
    }, [token, sourceObject, sourceIdData, getZipComponents]);

    // Re-opens an ancestor segment of the current breadcrumb trail, trimming the
    // trail to that point (mirrors clicking that node directly in the tree).
    const handleBreadcrumbClick = useCallback((index: number) => {
        const crumbs = activeFile.breadcrumb;
        if (!crumbs) return;
        const target = crumbs[index];
        if (!target?.path) return;
        handleComponentClick(target.groupName, target.path, target.label, crumbs.slice(0, index + 1));
    }, [activeFile.breadcrumb, handleComponentClick]);

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-content2">
            {activeFile?.breadcrumb && activeFile.breadcrumb.length > 0 && (
                <div className="flex-none px-4 py-2 bg-content1 border-b border-default-200 flex items-center justify-between gap-2">
                    <Breadcrumbs size="sm" separator=">">
                        {activeFile.breadcrumb.map((crumb, index) => {
                            const isLast = index === activeFile.breadcrumb!.length - 1;
                            return (
                                <BreadcrumbItem
                                    key={`${crumb.label}-${index}`}
                                    onPress={!isLast ? () => handleBreadcrumbClick(index) : undefined}
                                >
                                    {crumb.label}
                                </BreadcrumbItem>
                            );
                        })}
                    </Breadcrumbs>
                    {metadataSourceId ? <MetadataInfoButton sourceId={metadataSourceId} /> : null}
                </div>
            )}
            <div className="flex flex-1 overflow-hidden gap-2 p-2">
            {/* Left Panel: Component structure */}
            {leftPanelOpen ? (
                <ResizablePanel
                    width={leftPanelWidth}
                    height={Infinity}
                    minConstraints={[200, Infinity]}
                    maxConstraints={[500, Infinity]}
                    onResize={(e, { size }) => setLeftPanelWidth(size.width)}
                    resizeHandles={['e']}
                    handle={<div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-primary/20 transition-colors duration-200" />}
                    className="relative"
                >
                    <div className="w-full h-full flex flex-col bg-content1 rounded-lg border border-default-200 shadow-sm">
                        <div className="flex-none p-4 border-b border-default-200 flex justify-between items-center bg-content2 rounded-t-lg h-[52px]">
                            <h2 className="text-base font-medium text-foreground">Components</h2>
                            <button onClick={() => setLeftPanelOpen(false)} className="p-1 hover:bg-default-100 rounded-full" title="Collapse">
                                <Icon icon="hugeicons:layout-right" width="20" height="20" className="text-foreground" />
                            </button>
                        </div>

                        <div className="p-3 border-b border-default-200">
                            <div className="relative">
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                    placeholder="Search components..."
                                    value={leftPanelSearchQuery}
                                    onChange={(e) => setLeftPanelSearchQuery(e.target.value)}
                                />
                                <div className="absolute left-3 top-2.5 text-default-400">
                                    <Icon icon="ri:search-line" width="18" height="18" />
                                </div>
                                {leftPanelSearchQuery && (
                                    <button
                                        className="absolute right-3 top-2.5 text-default-400 hover:text-default-500"
                                        onClick={() => setLeftPanelSearchQuery('')}
                                    >
                                        <Icon icon="ri:close-line" width="18" height="18" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto overflow-x-auto">
                            {hierarchyData && hierarchyData.length > 0 ? (
                                <ComponentList
                                    data={hierarchyData}
                                    onComponentClick={handleComponentClick}
                                    activeFileName={activeFile?.name ?? ''}
                                    activeBreadcrumb={activeFile?.breadcrumb?.map(crumb => crumb.label)}
                                    searchQuery={leftPanelSearchQuery}
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <CtySpinner size="lg" />
                                </div>
                            )}
                        </div>
                    </div>
                </ResizablePanel>
            ) : (
                <div className="w-8 flex-shrink-0 flex items-start justify-center bg-content1 rounded-lg border border-default-200 shadow-sm">
                    <button onClick={() => setLeftPanelOpen(true)} className="p-2 hover:bg-default-100 rounded-lg h-[52px]" title="Expand">
                        <Icon icon="solar:hamburger-menu-outline" width="30" height="30" className="text-foreground" />
                    </button>
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 min-h-0 overflow-hidden bg-white rounded-lg border border-default-200 shadow-sm">
                <div className="h-full overflow-auto">
                    {renderContent()}
                </div>
            </div>

            {/* Right Panel: Component details */}
            {rightPanelOpen ? (
                <ResizablePanel
                    width={rightPanelWidth}
                    height={Infinity}
                    minConstraints={[250, Infinity]}
                    maxConstraints={[600, Infinity]}
                    onResize={(e, { size }) => setRightPanelWidth(size.width)}
                    resizeHandles={['w']}
                    handle={<div className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-primary/20 transition-colors duration-200" />}
                    className="relative"
                >
                    <div className="w-full h-full bg-content1 rounded-lg border border-default-200 shadow-sm">
                        {isLoading ? (
                            <div className="h-full flex flex-col">
                                <div className="flex-none p-4 border-b border-default-200 bg-content2 rounded-t-lg h-[52px]">
                                    <h2 className="text-base font-medium text-foreground">Component Details</h2>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                    <CtySpinner size="lg" />
                                </div>
                            </div>
                        ) : renderRightPanel()}
                    </div>
                </ResizablePanel>
            ) : (
                <div className="w-8  flex-shrink-0 flex items-start justify-center bg-content2 rounded-lg border border-default-200 shadow-sm">
                    <button onClick={() => setRightPanelOpen(true)} className="p-2 hover:bg-default-100 rounded-lg h-[52px]" title="Expand">
                        <Icon icon="solar:hamburger-menu-outline" width="30" height="30" className="text-foreground" />
                    </button>
                </div>
            )}
            </div>
        </div>
    );
}

// Recursively filters a component group tree against a search query.
// A group matching by name is kept whole; otherwise it's kept only if a
// descendant (component or nested sub-group) matches.
function filterGroupTree(group: ComponentGroup, query: string): ComponentGroup | null {
    if (!group || typeof group !== 'object') return null;

    const groupNameMatches = typeof group.component_name === 'string' &&
        group.component_name.toLowerCase().includes(query);
    if (groupNameMatches) return group;

    const filteredComponents = Array.isArray(group.components)
        ? group.components.filter(component =>
            component && typeof component.name === 'string' &&
            component.name.toLowerCase().includes(query))
        : [];

    const filteredSubGroups = getSubGroups(group)
        .map(subGroup => filterGroupTree(subGroup, query))
        .filter(Boolean) as ComponentGroup[];

    if (filteredComponents.length === 0 && filteredSubGroups.length === 0) {
        return null;
    }

    return {
        ...group,
        components: filteredComponents,
        sub_groups: filteredSubGroups,
    };
}

// Returns true if the group itself, any of its components, or any nested
// sub-group (at any depth) matches the active file name.
function groupContainsActiveFile(group: ComponentGroup, activeFileName: string): boolean {
    if (!group || !activeFileName) return false;
    if (group.component_name === activeFileName) return true;
    if (Array.isArray(group.components) && group.components.some(component => component && component.name === activeFileName)) {
        return true;
    }
    return getSubGroups(group).some(subGroup => groupContainsActiveFile(subGroup, activeFileName));
}

// The ComponentList component for the left panel
function ComponentList({ data, onComponentClick, activeFileName, activeBreadcrumb, searchQuery }: ComponentListProps) {
    // Filter the data based on search query
    const filteredData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        if (!searchQuery.trim()) return data;

        const query = searchQuery.toLowerCase().trim();

        return data
            .filter(group => group && typeof group === 'object')
            .map(group => filterGroupTree(group, query))
            .filter(Boolean) as ComponentGroup[];
    }, [data, searchQuery]);

    // Sort the filtered data alphabetically
    const sortedData = useMemo(() => getSortedComponentGroups(filteredData), [filteredData]);

    // Show a message when no results are found
    if (!sortedData || sortedData.length === 0) {
        return (
            <div className="px-4 py-6 text-center text-sm text-default-500">
                {searchQuery ? "No components match your search criteria" : "No components available"}
            </div>
        );
    }

    return (
        <div className="px-2 py-2">
            <ul className="space-y-1">
                {sortedData.map((group) => (
                    <ComponentGroupItem
                        key={group.component_name || 'unknown'}
                        group={group}
                        onComponentClick={onComponentClick}
                        activeFileName={activeFileName}
                        activeBreadcrumb={activeBreadcrumb}
                    />
                ))}
            </ul>
        </div>
    );
}

function ComponentGroupItem({ group, onComponentClick, activeFileName, activeBreadcrumb, level = 0, ancestorCrumbs = [] }: ComponentGroupItemProps) {
    const [expanded, setExpanded] = useState(false);

    // Auto-expand if this group (or any nested sub-group) contains the active component/diagram
    useEffect(() => {
        if (activeFileName && groupContainsActiveFile(group, activeFileName)) {
            setExpanded(true);
        }
    }, [activeFileName, group]);

    if (!group) {
        return null;
    }

    const components = Array.isArray(group.components) ? group.components : [];
    const subGroups = getSubGroups(group);
    const hasChildren = components.length > 0 || subGroups.length > 0;
    // Groups are always clickable, using the group's own name as the lookup so a
    // click always fires a request instead of silently doing nothing.
    const groupPath = group.component_name || '';
    // This group's own place in the hierarchy; components/sub-groups extend it by one more crumb.
    const ownCrumb: BreadcrumbCrumb = { label: group.component_name || 'Unknown', groupName: group.component_name || '', path: groupPath };
    const ownPath = [...ancestorCrumbs, ownCrumb];
    const ownLabels = ownPath.map(crumb => crumb.label);
    // Compare the FULL path, not just the bare name: a group and a same-named child
    // component (e.g. group "ACCESSORIES" containing a component also named "ACCESSORIES")
    // must not both light up when only one of them is actually open.
    const isGroupActive = isSamePath(activeBreadcrumb, ownLabels);

    const handleGroupNameClick = () => {
        if (!groupPath) return;
        onComponentClick(group.component_name || '', groupPath, group.component_name || '', ownPath);
    };

    return (
        <li className={level === 0 ? 'border-b border-default-200 pb-1 last:border-0' : ''}>
            <div className="flex items-center justify-between gap-1">
                <button
                    className={`
                        flex-1 text-left text-sm font-medium truncate rounded-md px-2 py-1.5
                        ${isGroupActive ? 'bg-primary text-white' : 'text-primary'}
                    `}
                    title={group.component_name || 'Unknown'}
                    onClick={handleGroupNameClick}
                >
                    {group.component_name || 'Unknown'}
                </button>
                {hasChildren && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="p-1 hover:bg-default-100 rounded-full flex-shrink-0"
                        title={expanded ? "Collapse" : "Expand"}
                    >
                        {expanded ? (
                            <Icon icon="lets-icons:expand-up" width="16" height="16" className="text-foreground" />
                        ) : (
                            <Icon icon="lets-icons:expand-down" width="16" height="16" className="text-foreground" />
                        )}
                    </button>
                )}
            </div>
            {expanded && hasChildren && (
                <ul className="ml-4 space-y-1">
                    {components.map((component, index) => {
                        // Skip rendering if component or component.name is null/undefined
                        if (!component || !component.name) {
                            return null;
                        }

                        const isLeafActive = isSamePath(activeBreadcrumb, [...ownLabels, component.name]);
                        // Falls back to the component's own name when the catalog
                        // returns an empty `path`, so the row stays selectable.
                        const componentPath = getComponentLookupPath(component);

                        return (
                            <li key={component.name || `component-${index}`}>
                                {index === 0 && component.name.toLowerCase().endsWith('.svg') ? (
                                    <span className="w-full text-left px-2 py-1.5 text-sm rounded-md text-default-400">
                                        {component.name}
                                    </span>
                                ) : (
                                    <button
                                        className={`
                                            w-full text-left px-2 py-1.5 text-sm rounded-md
                                            ${componentPath ? 'text-primary ' : 'text-default-400'}
                                            ${isLeafActive ? 'bg-primary text-white' : ''}
                                        `}
                                        onClick={() => componentPath && onComponentClick(group.component_name || '', componentPath, component.name, [...ownPath, { label: component.name, groupName: group.component_name || '', path: componentPath }])}
                                    >
                                        {component.name}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                    {subGroups.map((subGroup, index) => (
                        <ComponentGroupItem
                            key={subGroup?.component_name || `subgroup-${index}`}
                            group={subGroup}
                            onComponentClick={onComponentClick}
                            activeFileName={activeFileName}
                            activeBreadcrumb={activeBreadcrumb}
                            level={level + 1}
                            ancestorCrumbs={ownPath}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export default ZipFileExplorer;
